import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import NotificationPreferencesDrawer from './NotificationPreferencesDrawer';

// ─── Notification type config ─────────────────────────────────────────────────
const NOTIFICATION_CONFIG = {
  arrival_alert: {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
      </svg>
    ),
    color: 'text-green-500',
    bg: 'bg-green-500/10',
  },
  order_update: {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
      </svg>
    ),
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
  },
  earnings_payout: {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
      </svg>
    ),
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
  },
  system_message: {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 9h-2V5h2v6zm0 4h-2v-2h2v2z" />
      </svg>
    ),
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
  },
};

// ─── Time formatter ───────────────────────────────────────────────────────────
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  return date?.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' });
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  return date?.toDateString() === now?.toDateString();
};

// ─── Single Notification Item ─────────────────────────────────────────────────
const NotificationItem = ({ notification, onMarkRead }) => {
  const config = NOTIFICATION_CONFIG?.[notification?.type] || NOTIFICATION_CONFIG?.system_message;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0 ${!notification?.is_read ? 'bg-blue-50/40' : ''}`}
      onClick={() => !notification?.is_read && onMarkRead(notification?.id)}
    >
      {/* Icon */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${config?.bg} ${config?.color}`}>
        {config?.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${!notification?.is_read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
            {notification?.title}
          </p>
          <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">{formatTime(notification?.created_at)}</span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">{notification?.message}</p>
      </div>

      {/* Unread dot */}
      {!notification?.is_read && (
        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
      )}
    </div>
  );
};

// ─── Main NotificationPanel ───────────────────────────────────────────────────
const NotificationPanel = ({ isOpen, onClose, triggerRef }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const panelRef = useRef(null);
  const channelRef = useRef(null);

  // ─── Fetch notifications ────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) { setLoading(false); return; }

      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (!error) setNotifications(data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Real-time subscription ─────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    fetchNotifications();

    const setupSubscription = async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      channelRef.current = supabase
        ?.channel(`notifications:${user?.id}`)
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user?.id}`,
          },
          (payload) => {
            if (payload?.eventType === 'INSERT') {
              setNotifications(prev => [payload?.new, ...prev]);
            } else if (payload?.eventType === 'UPDATE') {
              setNotifications(prev =>
                prev?.map(n => n?.id === payload?.new?.id ? payload?.new : n)
              );
            } else if (payload?.eventType === 'DELETE') {
              setNotifications(prev => prev?.filter(n => n?.id !== payload?.old?.id));
            }
          }
        )
        ?.subscribe();
    };

    setupSubscription();

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
    };
  }, [isOpen, fetchNotifications]);

  // ─── Close on outside click ─────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        panelRef?.current && !panelRef?.current?.contains(e?.target) &&
        triggerRef?.current && !triggerRef?.current?.contains(e?.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, triggerRef]);

  // ─── Mark single as read ────────────────────────────────────────────────
  const handleMarkRead = async (id) => {
    try {
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', id);
      setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, is_read: true } : n));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // ─── Mark all as read ───────────────────────────────────────────────────
  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', user?.id)?.eq('is_read', false);
      setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    } finally {
      setMarkingAll(false);
    }
  };

  // ─── Group by Today / Earlier ───────────────────────────────────────────
  const todayNotifications = notifications?.filter(n => isToday(n?.created_at));
  const earlierNotifications = notifications?.filter(n => !isToday(n?.created_at));
  const unreadCount = notifications?.filter(n => !n?.is_read)?.length;

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-[1500] overflow-hidden"
      style={{ maxHeight: '520px', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-gray-900 text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <span className="bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              disabled={markingAll}
              className="text-xs text-blue-600 hover:text-blue-700 font-semibold disabled:opacity-50 transition-colors"
            >
              {markingAll ? 'Marking...' : 'Mark all read'}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3 h-3 text-gray-500">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(4)]?.map((_, i) => (
              <div key={i} className="flex items-start gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded-full w-3/4" />
                  <div className="h-2.5 bg-gray-100 rounded-full w-full" />
                  <div className="h-2.5 bg-gray-100 rounded-full w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications?.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-400">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-500">No notifications yet</p>
            <p className="text-xs text-gray-400 mt-1">You are all caught up!</p>
          </div>
        ) : (
          <>
            {todayNotifications?.length > 0 && (
              <>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Today</p>
                </div>
                {todayNotifications?.map(n => (
                  <NotificationItem key={n?.id} notification={n} onMarkRead={handleMarkRead} />
                ))}
              </>
            )}
            {earlierNotifications?.length > 0 && (
              <>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Earlier</p>
                </div>
                {earlierNotifications?.map(n => (
                  <NotificationItem key={n?.id} notification={n} onMarkRead={handleMarkRead} />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Bell Icon with Badge ─────────────────────────────────────────────────────
export const NotificationBell = ({ className = '', iconColor = 'currentColor', badgeColor = 'bg-red-500' }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const triggerRef = useRef(null);
  const channelRef = useRef(null);
  const longPressTimerRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      const { count } = await supabase
        ?.from('notifications')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id)
        ?.eq('is_read', false);
      setUnreadCount(count || 0);
    } catch (err) {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();

    const setupBadgeSubscription = async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      channelRef.current = supabase
        ?.channel(`notifications_badge:${user?.id}`)
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user?.id}`,
          },
          () => fetchUnreadCount()
        )
        ?.subscribe();
    };

    setupBadgeSubscription();

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
    };
  }, [fetchUnreadCount]);

  // ─── Long-press handlers ────────────────────────────────────────────────
  const handlePressStart = () => {
    longPressTimerRef.current = setTimeout(() => {
      setShowPreferences(true);
    }, 600);
  };

  const handlePressEnd = () => {
    if (longPressTimerRef?.current) {
      clearTimeout(longPressTimerRef?.current);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(prev => !prev)}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressEnd}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        aria-label="Notifications (long press for preferences)"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" className="w-5 h-5">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] ${badgeColor} text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        triggerRef={triggerRef}
      />

      <NotificationPreferencesDrawer
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
      />
    </div>
  );
};

export default NotificationPanel;
